import type { AxiosError } from 'axios';
import apiClient from '../client';
import type {
  QuizSaveRequest,
  QuizSavedResult,
  PatientQuiz,
  PendingApprovalsResponse,
  ApproveQuizRequest,
  ApproveQuizResponse,
} from '../types/quiz';
import type { ProductReportResponse } from '../types/report';

export const quizzesApi = {
  createPending: (data: { patientId: number; variantId: string }) =>
    apiClient.post<{ hash: string }>('/quizzes/create-pending', {
      patientId: data.patientId,
      variantId: data.variantId,
    }),

  saveResult: (data: QuizSaveRequest) =>
    apiClient.post<{ shareHash: string | null }>('/quizzes/save-result', data),

  getResult: (hash: string) => apiClient.get<QuizSavedResult>(`/quizzes/${hash}`),

  sendQuiz: (hash: string) => apiClient.post(`/quizzes/${hash}/send`),

  getPendingApprovals: () => apiClient.get<PendingApprovalsResponse>('/quizzes/pending-approvals'),

  approveQuiz: (hash: string, data: ApproveQuizRequest) =>
    apiClient.post<ApproveQuizResponse>(`/quizzes/${hash}/approve`, data),

  /**
   * Fetch the product report for an approved quiz.
   * API: GET /quizzes/{hash}/product-report
   * Returns the same ProductReportResponse shape as the lab-based formulations report.
   */
  getQuizProductReport: (hash: string) =>
    apiClient.get<ProductReportResponse>(`/quizzes/${hash}/product-report`),

  /**
   * Fetch quizzes for a patient.
   * Returns an empty array when the endpoint responds with 404 or is
   * unavailable, so callers can render "No quizzes found" gracefully.
   */
  listByPatient: async (patientId: number) => {
    try {
      return await apiClient.get<PatientQuiz[]>(`/quizzes/patient/${patientId}`);
    } catch (err) {
      const axiosErr = err as AxiosError;
      if (axiosErr.response?.status === 404 || axiosErr.response?.status === 401) {
        return { data: [] as PatientQuiz[] };
      }
      throw err;
    }
  },
};
